import React, { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Lock, RefreshCw, Search, Send, CheckCircle2, Mail, Users } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { key: "created_at", label: "Applied" },
  { key: "firstName", label: "First name" },
  { key: "email", label: "Email" },
  { key: "device", label: "Device" },
  { key: "status", label: "Status" },
  { key: "relationship", label: "Relationship" },
  { key: "willing", label: "21-day" },
  { key: "consent", label: "Consent" },
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const AdminPage = () => {
  const [token, setToken] = useState(sessionStorage.getItem("renn_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invitedFilter, setInvitedFilter] = useState("all");
  const [settings, setSettings] = useState({ digest_mode: false, last_digest_at: null, digest_day: 0, digest_hour: 9, capacity: 500 });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [capacityInput, setCapacityInput] = useState("");
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [inviting, setInviting] = useState("");
  const { toast } = useToast();

  const authHeader = (t) => ({ headers: { "X-Admin-Token": t } });

  const load = useCallback(
    async (t) => {
      setLoading(true);
      setError("");
      try {
        const [appsRes, setRes] = await Promise.all([
          axios.get(`${API}/admin/applicants`, authHeader(t)),
          axios.get(`${API}/admin/settings`, authHeader(t)),
        ]);
        setRows(appsRes.data.applicants || []);
        setSettings(setRes.data);
        setCapacityInput(String(setRes.data.capacity ?? 500));
        setAuthed(true);
        sessionStorage.setItem("renn_admin_token", t);
      } catch (err) {
        if (err?.response?.status === 401) setError("Invalid admin token.");
        else if (err?.response?.status === 503) setError("Admin access is not configured on the server.");
        else setError("Could not load applicants. Please try again.");
        setAuthed(false);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fmt = (k, v) => {
    if (k === "created_at" && v) {
      try { return new Date(v).toLocaleString(); } catch { return v; }
    }
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return v ?? "";
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (invitedFilter === "invited" && !r.invited) return false;
      if (invitedFilter === "not" && r.invited) return false;
      if (q) {
        const hay = `${r.firstName || ""} ${r.email || ""} ${r.relationship || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, query, statusFilter, invitedFilter]);

  const invite = async (email) => {
    setInviting(email);
    try {
      await axios.post(`${API}/admin/invite`, { email }, authHeader(token));
      setRows((rs) => rs.map((r) => (r.email === email ? { ...r, invited: true } : r)));
      toast({ title: "Invite sent", description: `A "You're in!" email was sent to ${email}.` });
    } catch (err) {
      toast({ title: "Could not send invite", description: "Please try again.", variant: "destructive" });
    } finally {
      setInviting("");
    }
  };

  const toggleDigest = async () => {
    const next = !settings.digest_mode;
    try {
      await axios.post(`${API}/admin/settings`, { digest_mode: next }, authHeader(token));
      setSettings((s) => ({ ...s, digest_mode: next }));
      toast({ title: next ? "Weekly digest on" : "Per-applicant emails on", description: next ? "You'll get one weekly summary instead of one email per signup." : "You'll be emailed for each new applicant." });
    } catch {
      toast({ title: "Could not update setting", variant: "destructive" });
    }
  };

  const sendDigestNow = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/digest`, {}, authHeader(token));
      toast({ title: "Digest sent", description: `Summary of ${data.count} new signup(s) emailed to you.` });
      load(token);
    } catch {
      toast({ title: "Could not send digest", variant: "destructive" });
    }
  };

  const saveCapacity = async () => {
    const val = parseInt(capacityInput, 10);
    if (Number.isNaN(val) || val < 0) {
      toast({ title: "Enter a valid number of spots", variant: "destructive" });
      return;
    }
    setSavingCapacity(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/settings`,
        { capacity: val },
        authHeader(token)
      );
      setSettings(data);
      setCapacityInput(String(data.capacity));
      toast({ title: "Spots updated", description: `This round now has ${val.toLocaleString()} total spots.` });
    } catch {
      toast({ title: "Could not update spots", variant: "destructive" });
    } finally {
      setSavingCapacity(false);
    }
  };

  const saveSchedule = async (day, hour) => {
    setSavingSchedule(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/settings`,
        { digest_day: day, digest_hour: hour },
        authHeader(token)
      );
      setSettings(data);
      toast({ title: "Schedule saved", description: `Weekly digest set for ${DAYS[day]} at ${String(hour).padStart(2, "0")}:00 UTC.` });
    } catch {
      toast({ title: "Could not save schedule", variant: "destructive" });
    } finally {
      setSavingSchedule(false);
    }
  };

  const exportCsv = () => {
    const cols = [...COLUMNS, { key: "invited", label: "Invited" }];
    const header = cols.map((c) => c.label).join(",");
    const lines = filtered.map((r) =>
      cols.map((c) => {
        const val = String(fmt(c.key, r[c.key]) ?? "").replace(/"/g, '""');
        return `"${val}"`;
      }).join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `renn-beta-applicants-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectCls = "bg-[#0A1220] border border-[#29354A] rounded-lg px-3 py-2 text-sm text-[#F5F7FB] outline-none focus:border-[#10C98A]/60";

  return (
    <div className="renn-bg min-h-screen">
      <div className="relative z-10">
        <header className="border-b border-[#29354A] bg-[#050A12]/80 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
            <Link to="/" className="inline-flex items-center">
              <img src="/renn-logo.png" alt="Renn" className="h-7 lg:h-8 w-auto" draggable={false} />
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8290A8] hover:text-[#13D99A] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 rounded-xl bg-[#10C98A]/15 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#13D99A]" />
            </span>
            <div>
              <h1 className="font-display text-2xl font-bold text-[#F5F7FB]">Beta applicants</h1>
              <p className="text-sm text-[#8290A8]">Private admin view</p>
            </div>
          </div>

          {!authed ? (
            <div className="max-w-md rounded-2xl border border-[#29354A] bg-[#121A28] p-6">
              <label className="block text-sm text-[#F5F7FB] mb-2">Admin token</label>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && token && load(token)}
                placeholder="Enter admin token"
                className="w-full bg-[#0A1220] border border-[#29354A] rounded-lg px-4 py-3 text-[#F5F7FB] placeholder:text-[#8290A8]/60 outline-none focus:border-[#10C98A]/60"
              />
              {error && <p className="mt-3 text-sm text-[#FF4D4F]">{error}</p>}
              <button
                onClick={() => token && load(token)}
                disabled={loading || !token}
                className="btn-emerald mt-4 w-full bg-[#10C98A] text-[#050A12] font-semibold py-3 rounded-full disabled:opacity-60"
              >
                {loading ? "Checking\u2026" : "Unlock"}
              </button>
            </div>
          ) : (
            <>
              {/* Available spots control */}
              <div className="rounded-2xl border border-[#29354A] bg-[#121A28] p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-[#F5A623]/15 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#F5A623]" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-[#F5F7FB]">Available spots this round</p>
                    <p className="text-xs text-[#8290A8]">
                      {rows.length.toLocaleString()} applied ·{" "}
                      <span className="text-[#13D99A]">{Math.max((settings.capacity || 0) - rows.length, 0).toLocaleString()} spots left</span>
                      {" "}· shown as the public "spots left" nudge
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-[#8290A8]">Total spots</label>
                  <input
                    type="number"
                    min="0"
                    value={capacityInput}
                    onChange={(e) => setCapacityInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveCapacity()}
                    className="w-28 bg-[#0A1220] border border-[#29354A] rounded-lg px-3 py-2 text-sm text-[#F5F7FB] outline-none focus:border-[#10C98A]/60"
                  />
                  <button
                    onClick={saveCapacity}
                    disabled={savingCapacity}
                    className="btn-emerald text-sm font-semibold text-[#050A12] bg-[#10C98A] rounded-full px-4 py-2 disabled:opacity-60"
                  >
                    {savingCapacity ? "Saving\u2026" : "Save spots"}
                  </button>
                </div>
              </div>

              {/* Digest controls */}
              <div className="rounded-2xl border border-[#29354A] bg-[#121A28] p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-[#10C98A]/15 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#13D99A]" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-[#F5F7FB]">
                      Owner emails: {settings.digest_mode ? "Weekly digest" : "One per applicant"}
                    </p>
                    <p className="text-xs text-[#8290A8]">
                      {settings.last_digest_at ? `Last digest: ${new Date(settings.last_digest_at).toLocaleString()}` : "No digest sent yet"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={toggleDigest} className="text-sm text-[#F5F7FB] border border-[#29354A] rounded-full px-4 py-2 hover:border-[#10C98A]/50 transition-colors">
                    {settings.digest_mode ? "Switch to per-applicant" : "Switch to weekly digest"}
                  </button>
                  <button onClick={sendDigestNow} className="btn-emerald inline-flex items-center gap-2 text-sm font-semibold text-[#050A12] bg-[#10C98A] rounded-full px-4 py-2">
                    <Send className="w-4 h-4" /> Send digest now
                  </button>
                </div>

                {settings.digest_mode && (
                  <div className="w-full border-t border-[#29354A] pt-4 flex flex-wrap items-center gap-3">
                    <span className="text-sm text-[#8290A8]">Deliver weekly on</span>
                    <select
                      value={settings.digest_day}
                      onChange={(e) => saveSchedule(Number(e.target.value), settings.digest_hour)}
                      disabled={savingSchedule}
                      className="bg-[#0A1220] border border-[#29354A] rounded-lg px-3 py-2 text-sm text-[#F5F7FB] outline-none focus:border-[#10C98A]/60"
                    >
                      {DAYS.map((d, i) => (
                        <option key={d} value={i}>{d}</option>
                      ))}
                    </select>
                    <span className="text-sm text-[#8290A8]">at</span>
                    <select
                      value={settings.digest_hour}
                      onChange={(e) => saveSchedule(settings.digest_day, Number(e.target.value))}
                      disabled={savingSchedule}
                      className="bg-[#0A1220] border border-[#29354A] rounded-lg px-3 py-2 text-sm text-[#F5F7FB] outline-none focus:border-[#10C98A]/60"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                      ))}
                    </select>
                    <span className="text-sm text-[#8290A8]">UTC</span>
                    <span className="text-xs text-[#8290A8]/70">
                      Currently: {DAYS[settings.digest_day]} at {String(settings.digest_hour).padStart(2, "0")}:00 UTC
                    </span>
                  </div>
                )}
              </div>

              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="w-4 h-4 text-[#8290A8] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search name, email, relationship…"
                    className="w-full bg-[#0A1220] border border-[#29354A] rounded-lg pl-9 pr-3 py-2 text-sm text-[#F5F7FB] placeholder:text-[#8290A8]/60 outline-none focus:border-[#10C98A]/60"
                  />
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
                  <option value="all">All statuses</option>
                  <option value="military">Military</option>
                  <option value="civilian">Civilian</option>
                </select>
                <select value={invitedFilter} onChange={(e) => setInvitedFilter(e.target.value)} className={selectCls}>
                  <option value="all">All applicants</option>
                  <option value="invited">Invited</option>
                  <option value="not">Not invited</option>
                </select>
                <button onClick={() => load(token)} className="inline-flex items-center gap-2 text-sm text-[#F5F7FB] border border-[#29354A] rounded-full px-4 py-2 hover:border-[#10C98A]/50 transition-colors">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
                <button onClick={exportCsv} disabled={!filtered.length} className="btn-emerald inline-flex items-center gap-2 text-sm font-semibold text-[#050A12] bg-[#10C98A] rounded-full px-4 py-2 disabled:opacity-60">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              </div>

              <p className="text-sm text-[#8290A8] mb-3">
                Showing <span className="font-semibold text-[#F5F7FB]">{filtered.length}</span> of {rows.length}
              </p>

              <div className="rounded-2xl border border-[#29354A] bg-[#121A28] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#8290A8] border-b border-[#29354A]">
                      {COLUMNS.map((c) => (
                        <th key={c.key} className="px-4 py-3 font-medium whitespace-nowrap">{c.label}</th>
                      ))}
                      <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={COLUMNS.length + 1} className="px-4 py-10 text-center text-[#8290A8]">
                          {rows.length === 0 ? "No applicants yet." : "No matches for your filters."}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r, i) => (
                        <tr key={i} className="border-b border-[#29354A]/50 text-[#F5F7FB]">
                          {COLUMNS.map((c) => (
                            <td key={c.key} className="px-4 py-3 whitespace-nowrap">{fmt(c.key, r[c.key])}</td>
                          ))}
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            {r.invited ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#13D99A]">
                                <CheckCircle2 className="w-4 h-4" /> Invited
                              </span>
                            ) : (
                              <button
                                onClick={() => invite(r.email)}
                                disabled={inviting === r.email}
                                className="btn-emerald inline-flex items-center gap-1.5 text-xs font-semibold text-[#050A12] bg-[#10C98A] rounded-full px-3 py-1.5 disabled:opacity-60"
                              >
                                <Send className="w-3.5 h-3.5" /> {inviting === r.email ? "Sending\u2026" : "Invite"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
